<template>
  <nav class="navbar" role="navigation" aria-label="main navigation">
    <div class="navbar-brand">
      <div class="navbar-item is-hidden get-quote-mobile-bt">
        <div class="buttons">
          <a
            :href="quoteHref"
            class="button ps-button get-quote-bt short is-inverted"
            data-test="nav-quote-mobile"
            @click="onQuoteClick"
          >
            <span>Get your personal quote</span>
          </a>
        </div>
      </div>
      <div class="ps-logo-container is-hidden-desktop">
        <nuxt-link no-prefetch to="/" class="ps-logo">
          <img
            src="~/assets/images/Pedalsure-Logo_Large.svg"
            alt="Pedalsure"
            width="1080"
            height="173"
            class="ps-wordmark"
        /></nuxt-link>
      </div>
      <a
        role="button"
        class="navbar-burger burger"
        aria-label="menu"
        aria-expanded="false"
        data-target="navbarBasicExample"
        @click="toggleMenu"
      >
        <span aria-hidden="true" />
        <span aria-hidden="true" />
        <span aria-hidden="true" />
      </a>
    </div>

    <div
      id="navbarBasicExample"
      :class="{ 'is-active': isActive, 'navbar-menu': true }"
    >
      <!-- Mobile drawer. Kept separate from the desktop bar below so the phone
           menu can carry more than a bare list of links without compromising
           the header layout. -->
      <div class="drawer is-hidden-desktop">
        <div class="drawer-head">
          <nuxt-link
            no-prefetch
            to="/"
            class="drawer-logo"
            @click.native="closeMenu"
          >
            <img
              src="~/assets/images/Pedalsure-Logo_Large.svg"
              alt="Pedalsure"
              width="1080"
              height="173"
            />
          </nuxt-link>
          <button
            class="drawer-close"
            aria-label="Close menu"
            @click="closeMenu"
          >
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path
                d="M6.5 6.5l11 11M17.5 6.5l-11 11"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
              />
            </svg>
          </button>
        </div>

        <template v-if="!isInPurchaseFunnel">
          <a
            :href="quoteHref"
            class="button ps-button is-fullwidth call-to-action drawer-cta"
            data-test="nav-quote-drawer"
            @click="onQuoteClick"
          >
            Get your personal quote
          </a>
          <p class="drawer-cta-note">
            Takes about two minutes · no payment today
          </p>
        </template>

        <p class="drawer-proof">
          <RatingStars :size="14" />
          <span
            ><b>{{ ratingTextShort }}</b> · 50,000+ cyclists covered</span
          >
        </p>

        <ul class="drawer-links">
          <li>
            <nuxt-link no-prefetch to="/" @click.native="closeMenu">
              <span class="drawer-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" focusable="false">
                  <path
                    d="M4 11l8-6 8 6v8.5a.5.5 0 0 1-.5.5H15v-6H9v6H4.5a.5.5 0 0 1-.5-.5V11z"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.7"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </span>
              <span class="drawer-link-text">
                <span class="drawer-link-title">Home</span>
                <span class="drawer-link-copy">
                  Theft and accidental damage cover
                </span>
              </span>
              <svg
                class="drawer-chevron"
                viewBox="0 0 24 24"
                aria-hidden="true"
                focusable="false"
              >
                <path
                  d="M9.5 5.5l6.5 6.5-6.5 6.5"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </nuxt-link>
          </li>
          <li>
            <BikeInsuranceNavDropdown mode="mobile" />
          </li>
          <li v-for="link in drawerLinks" :key="link.to">
            <nuxt-link no-prefetch :to="link.to" @click.native="closeMenu">
              <span class="drawer-icon" aria-hidden="true">
                <svg viewBox="0 0 24 24" focusable="false">
                  <path
                    v-for="(d, i) in link.paths"
                    :key="i"
                    :d="d"
                    fill="none"
                    stroke="currentColor"
                    stroke-width="1.7"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                  />
                </svg>
              </span>
              <span class="drawer-link-text">
                <span class="drawer-link-title">{{ link.title }}</span>
                <span class="drawer-link-copy">{{ link.copy }}</span>
              </span>
              <svg
                class="drawer-chevron"
                viewBox="0 0 24 24"
                aria-hidden="true"
                focusable="false"
              >
                <path
                  d="M9.5 5.5l6.5 6.5-6.5 6.5"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </nuxt-link>
          </li>
        </ul>

        <div class="drawer-account">
          <template v-if="isAuthenticated">
            <nuxt-link
              no-prefetch
              to="/dashboard"
              class="button ps-button is-outlined is-inverted"
              @click.native="closeMenu"
            >
              Dashboard
            </nuxt-link>
            <button
              class="button ps-button is-outlined is-inverted"
              @click="logout"
            >
              Log out
            </button>
          </template>
          <nuxt-link
            v-else
            no-prefetch
            to="/login"
            class="button ps-button is-outlined is-inverted is-fullwidth"
            @click.native="closeMenu"
          >
            Log in to your account
          </nuxt-link>
        </div>

        <!-- Help sits last: the people who need it will look for it, and it
             shouldn't push the quote CTA down the screen. -->
        <div class="drawer-help">
          <p class="drawer-help-title">Need a hand?</p>
          <button class="drawer-help-row" @click="openChat">
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path
                d="M20 12a7.5 7.5 0 0 1-10.9 6.7L4.5 20l1.3-4.6A7.5 7.5 0 1 1 20 12z"
                fill="none"
                stroke="currentColor"
                stroke-width="1.7"
                stroke-linejoin="round"
              />
            </svg>
            Chat with support
          </button>
          <a class="drawer-help-row" href="tel:08008886745">
            <svg viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path
                d="M6.5 4h3l1.5 3.7-2 1.4a11 11 0 0 0 5.9 5.9l1.4-2L20 14.5v3a2 2 0 0 1-2.2 2A15.5 15.5 0 0 1 4 6.2 2 2 0 0 1 6 4z"
                fill="none"
                stroke="currentColor"
                stroke-width="1.7"
                stroke-linejoin="round"
              />
            </svg>
            0800 888 6745
            <span class="drawer-help-note">Mon–Fri, 9am–5pm</span>
          </a>
        </div>
      </div>

      <div class="navbar-desktop is-hidden-touch">
        <div class="navbar-desktop-left">
          <nuxt-link no-prefetch to="/" class="ps-logo navbar-desktop-logo">
            <img
              src="~/assets/images/Pedalsure-Logo_Large.svg"
              alt="Pedalsure"
              width="1080"
              height="173"
              class="ps-wordmark"
            />
          </nuxt-link>
          <div class="navbar-desktop-links">
            <div class="navbar-item">
              <BikeInsuranceNavDropdown mode="desktop" />
            </div>
            <nuxt-link
              no-prefetch
              to="/policy-information"
              class="navbar-item main-link"
              @click.native="closeMenu"
            >
              Policy
            </nuxt-link>
            <nuxt-link
              no-prefetch
              to="/faq"
              class="navbar-item main-link"
              @click.native="closeMenu"
            >
              FAQs
            </nuxt-link>
          </div>
        </div>

        <div class="navbar-desktop-right">
          <div
            v-if="isAuthenticated"
            class="nav-account"
            :class="{ 'is-open': accountMenuOpen }"
            @mouseenter="openAccountMenu"
            @mouseleave="scheduleCloseAccountMenu"
            @focusout="onAccountFocusOut"
          >
            <button
              ref="accountTrigger"
              type="button"
              class="nav-account-trigger"
              aria-haspopup="true"
              :aria-expanded="accountMenuOpen ? 'true' : 'false'"
              aria-controls="account-nav-panel"
              @click="onAccountTriggerClick"
              @keydown.esc="closeAccountMenu"
            >
              <span class="nav-account-avatar" aria-hidden="true">{{
                accountInitial
              }}</span>
              <span class="nav-account-name">{{ accountFirstName }}</span>
              <svg
                class="nav-dd-chevron"
                viewBox="0 0 24 24"
                aria-hidden="true"
                focusable="false"
              >
                <path
                  d="M6 9.5l6 6 6-6"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="2"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </button>
            <ul
              v-show="accountMenuOpen"
              id="account-nav-panel"
              class="nav-account-panel"
            >
              <li>
                <nuxt-link
                  no-prefetch
                  to="/dashboard"
                  @click.native="closeAccountMenu"
                >
                  Dashboard
                </nuxt-link>
              </li>
              <li>
                <nuxt-link
                  no-prefetch
                  to="/policy-information"
                  @click.native="closeAccountMenu"
                >
                  Policy documents
                </nuxt-link>
              </li>
              <li>
                <button type="button" @click="logoutFromAccountMenu">
                  Log out
                </button>
              </li>
            </ul>
          </div>
          <nuxt-link
            v-else
            no-prefetch
            to="/login"
            class="navbar-item main-link login-link"
          >
            Log in
          </nuxt-link>

          <div v-if="!isInPurchaseFunnel" class="buttons">
            <a
              :href="quoteHref"
              class="button ps-button get-quote-bt is-inverted"
              data-test="nav-quote-desktop"
              @click="onQuoteClick"
            >
              <span>Get your personal quote</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  </nav>
</template>

<script>
/* eslint-disable no-console */
import { mapActions } from 'vuex'
import { auth } from '~/services/firebase'
import { isPurchaseFunnelPath } from '@/helpers'
import { quotePath } from '@/helpers/estimator'
import BikeInsuranceNavDropdown from '@/components/common/BikeInsuranceNavDropdown'
import RatingStars from '@/components/RatingStars'
import { RATING_TEXT_SHORT } from '@/helpers/rating'

// The bike-type landing pages. The nav appears on every page, so it reads the
// type from the route rather than taking a prop — that keeps the six pages
// from having to thread a value through the layout to reach it.
const LANDING_PAGE_BIKE_TYPES = {
  '/electric-bike-insurance': 'electric',
  '/road-bike-insurance': 'road',
  '/mountain-bike-insurance': 'mountain',
  '/cargo-bike-insurance': 'cargo',
  '/commuter-cycle-insurance': 'commuter',
  '/triathlon-cycle-insurance': 'triathlon',
}

export default {
  name: 'MainNavigation',
  components: { BikeInsuranceNavDropdown, RatingStars },
  data() {
    return {
      ratingTextShort: RATING_TEXT_SHORT,
      isActive: false,
      accountMenuOpen: false,
      accountCloseTimer: null,
      // A line of context under each label, so the menu answers "what's behind
      // this?" instead of making people tap to find out.
      //
      // Home and the "Bike insurance" dropdown are hardcoded directly in the
      // template above this list — Home because it's a fixed first row, and
      // the dropdown because it isn't a plain link.
      //
      // "Policy" (-> /policy-information) was dropped from here when the
      // dropdown above was added, on the theory that "What we cover" ->
      // /what-we-cover replaced it. It didn't — that's a different page — so
      // it's back, under its old label (naming it "What's covered" again
      // would now read as a near-duplicate of "What we cover").
      drawerLinks: [
        {
          to: '/policy-information',
          title: 'Policy',
          copy: 'Policy details, limits and exclusions',
          paths: [
            'M6 3.5h8L18.5 8v12a.5.5 0 0 1-.5.5H6a.5.5 0 0 1-.5-.5V4a.5.5 0 0 1 .5-.5z',
            'M13.5 3.7V8.5h4.8',
            'M8.5 13h7M8.5 16.5h4.5',
          ],
        },
        {
          to: '/price-match-promise',
          title: 'Price Match Promise',
          copy: 'Same cover cheaper? We’ll match it',
          paths: ['M11 3.5H4.5V10l9.5 9.5 6.5-6.5L11 3.5z', 'M7.9 7.1h.01'],
        },
        {
          to: '/hiplok',
          title: 'Hiplok offer',
          copy: 'Up to 40% off approved locks',
          paths: [
            'M5.5 10.5h13a1 1 0 0 1 1 1v7a1 1 0 0 1-1 1h-13a1 1 0 0 1-1-1v-7a1 1 0 0 1 1-1z',
            'M8 10.5V7.5a4 4 0 0 1 8 0v3',
          ],
        },
        {
          to: '/claim',
          title: 'Make a claim',
          copy: 'Claims assessed within 24 hours',
          paths: [
            'M12 3.2l7 2.8v6c0 4.2-2.9 7.6-7 9-4.1-1.4-7-4.8-7-9V6l7-2.8z',
            'M9 12l2.2 2.2L15.2 10',
          ],
        },
        {
          to: '/faq',
          title: 'FAQs',
          copy: 'Answers to the questions we get most',
          paths: [
            'M12 3.8a8.2 8.2 0 1 1 0 16.4 8.2 8.2 0 0 1 0-16.4z',
            'M9.6 9.4a2.5 2.5 0 1 1 3.3 2.4c-.6.2-.9.7-.9 1.3v.5',
            'M12 16.6h.01',
          ],
        },
      ],
    }
  },
  computed: {
    isAuthenticated() {
      return this.$store.getters['user/isAuthenticated']
    },
    userEmail() {
      return this.$store.getters['user/userEmail']
    },
    // Firebase's displayName is only set for social sign-in — for a plain
    // email/password account it's empty, so this falls back to the part of
    // the address before the @ rather than showing a blank name.
    accountFirstName() {
      const user = this.$store.getters['user/user']
      const displayName = ((user && user.displayName) || '').trim()
      if (displayName) {
        return displayName.split(/\s+/)[0]
      }
      return (this.userEmail || '').split('@')[0]
    },
    accountInitial() {
      return (this.accountFirstName || '?').charAt(0).toUpperCase()
    },
    // Once someone is inside the purchase funnel, a "get a quote" button only
    // offers to throw away the quote they're partway through buying.
    isInPurchaseFunnel() {
      return isPurchaseFunnelPath(this.$route.path)
    },
    // The nav is sitewide, so the bike type comes from the route rather than
    // from a prop — only the bike-specific landing pages have one, and
    // everywhere else this is empty and the CTA points at a plain /quote.
    navBikeType() {
      return LANDING_PAGE_BIKE_TYPES[this.$route.path] || ''
    },
    // Distinguishes a quote started from the nav from one started by the
    // page's own hero CTA, which reports as `lp-<type>`.
    navSource() {
      return this.navBikeType ? `nav-${this.navBikeType}` : 'nav'
    },
    // The no-JavaScript destination for every "Get your personal quote" in
    // this nav. These were <button>s with no href at all, so a crawler, a
    // middle-click or a visitor whose JS hadn't run yet got nothing.
    quoteHref() {
      return quotePath({ bike: this.navBikeType, src: this.navSource })
    },
  },
  watch: {
    $route() {
      this.isActive = false
      document.body.style.overflow = ''
      this.closeAccountMenu()
    },
  },
  mounted() {
    document.addEventListener('click', this.onDocumentClick)
  },
  beforeDestroy() {
    document.removeEventListener('click', this.onDocumentClick)
    this.clearAccountCloseTimer()
  },
  methods: {
    ...mapActions('quote', {
      resetQuote: 'resetQuote',
    }),
    toggleMenu() {
      this.isActive = !this.isActive
      document.body.style.overflow = this.isActive ? 'hidden' : ''
    },
    closeMenu() {
      this.isActive = false
      document.body.style.overflow = ''
    },
    switchTheme(color) {
      this.$store.dispatch('theme/setTheme', color)
    },
    // The modal stays the fast path, but only for a plain left click — a
    // modified click is the visitor asking for a new tab, and swallowing it
    // would leave them on the page they are already on.
    onQuoteClick(event) {
      if (
        event.metaKey ||
        event.ctrlKey ||
        event.shiftKey ||
        event.altKey ||
        event.button !== 0
      ) {
        return
      }
      event.preventDefault()
      this.closeMenu()
      this.openQuickQuteModal()
    },
    openQuickQuteModal() {
      this.resetQuote()
      this.$modal.show('quick-quote', {
        // Empty on most pages, which is correct: the nav only knows the bike
        // type on the landing pages that are about one.
        bikeType: this.navBikeType,
        src: this.navSource,
      })
    },
    openChat() {
      if (typeof zE === 'function') {
        zE('messenger', 'show')
        zE('messenger', 'open')
      }
      this.closeMenu()
    },
    async performLogout() {
      try {
        await this.$store.dispatch('user/logout')
        this.$router.push({ path: '/', query: { ...this.$route.query } })
      } catch (err) {
        console.log('users/logout error', err)
      }
    },
    // Mobile drawer's log-out button — closes the drawer on the way out.
    async logout() {
      this.toggleMenu()
      await this.performLogout()
    },
    // Desktop account dropdown's log-out button — there's no mobile drawer
    // open to close here, so it only closes its own panel.
    async logoutFromAccountMenu() {
      this.closeAccountMenu()
      await this.performLogout()
    },
    async currentUser() {
      const user = await auth.currentUser
      console.log(user)
    },
    // --- Desktop account dropdown -------------------------------------
    // Same hover-with-a-grace-period pattern as BikeInsuranceNavDropdown:
    // trigger and panel are visually separated, so an instant close on
    // mouseleave would fire while the cursor is still crossing that gap,
    // before it can ever reach a link.
    clearAccountCloseTimer() {
      if (this.accountCloseTimer) {
        clearTimeout(this.accountCloseTimer)
        this.accountCloseTimer = null
      }
    },
    openAccountMenu() {
      this.clearAccountCloseTimer()
      this.accountMenuOpen = true
    },
    closeAccountMenu() {
      this.clearAccountCloseTimer()
      this.accountMenuOpen = false
    },
    scheduleCloseAccountMenu() {
      this.clearAccountCloseTimer()
      this.accountCloseTimer = setTimeout(() => {
        this.accountCloseTimer = null
        this.accountMenuOpen = false
      }, 300)
    },
    onAccountTriggerClick() {
      if (this.accountMenuOpen) {
        this.closeAccountMenu()
      } else {
        this.openAccountMenu()
      }
    },
    onAccountFocusOut(event) {
      const next = event.relatedTarget
      if (!next || !this.$el.contains(next)) {
        this.closeAccountMenu()
      }
    },
    onDocumentClick(event) {
      if (
        this.accountMenuOpen &&
        this.$refs.accountTrigger &&
        !this.$refs.accountTrigger
          .closest('.nav-account')
          .contains(event.target)
      ) {
        this.closeAccountMenu()
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.navbar {
  margin: 15px 0 10px 0;
  // height: 85px;
  padding: 0 20px;
  @include tablet {
    margin: 30px 0;
    // height: 85px;
    padding: 0 35px;
  }
  @include desktop {
    // height: 130px;
    margin: 40px 0;
    padding: 0 50px;
  }
}
.navbar-burger {
  // height: 85px;
  & span {
    right: 0;
    width: 20px;
    // left: inherit;
  }
}
// Was 0.8rem (12.8px) — below the 15px floor, and the smallest text in the
// header. Nav links now sit at the standard UI size.
.main-link {
  font-size: 17px;
  font-weight: 500;
  color: $ps-body-color;
  padding-left: 14px;
  padding-right: 14px;
  transition: color 120ms ease;

  &:hover {
    color: $ps-primary;
  }

  // Current page reads as indigo.
  &.nuxt-link-exact-active {
    color: $ps-primary;
    font-weight: 600;
  }
}
.ps-button {
  margin-left: 10px;
  margin-right: 10px;
}
.close-button {
  filter: brightness(0) invert(1);
}
.get-quote-mobile-bt {
  width: 65px;
  padding: 0;
  @include mobile {
    width: 20%;
    max-width: 120px;
    .buttons {
      width: 100%;
      .button {
        width: 100%;
        font-size: 0.7rem;
      }
    }
  }
}
.ps-logo-container {
  position: absolute;
  width: 150px;
  left: calc(50% - 75px);
  // Was `bottom: 5px`, which pinned the logo to the bottom of the navbar
  // rather than centring it. The old wordmark was short enough to get away
  // with it; this centres on the row instead.
  top: 50%;
  transform: translateY(-50%);
  text-align: center;
  @include desktop {
    width: 300px;
    left: calc(50% - 150px);
  }
  .ps-logo {
    color: $ps-gray;
    font-size: 1.5rem;
    font-family: $family-regular;
    @include tablet {
      font-size: 2rem;
    }
    @include desktop {
    }
  }
  // Original wordmark SVG, sized up from its old 130px. Its viewBox is tight
  // to the glyphs, so the box the browser centres is the ink itself.
  // width/height are on the tag so the header reserves the right space before
  // it loads.
  .ps-wordmark {
    display: block;
    width: 160px;
    height: auto;
    margin: 0 auto;

    @include tablet {
      width: 185px;
    }
  }
  // z-index: -1;
}

// --- Desktop bar ---------------------------------------------------------
// Replaces the old navbar-start / centred-logo / navbar-end trio: logo and
// links grouped on the left, account area and CTA on the right, all in one
// flex row instead of an absolutely-positioned logo floating over the rest.
.navbar-desktop {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
}
.navbar-desktop-left {
  display: flex;
  align-items: center;
  gap: 36px;
}
.navbar-desktop-logo {
  display: block;
  color: $ps-gray;

  .ps-wordmark {
    display: block;
    width: 185px;
    height: auto;
  }
}
.navbar-desktop-links {
  display: flex;
  align-items: center;
}
.navbar-desktop-right {
  display: flex;
  align-items: center;
}
.login-link {
  padding-right: 20px;
}

// --- Account dropdown (desktop only) — same disclosure pattern as
// BikeInsuranceNavDropdown (real <button aria-haspopup>, hover-open with a
// debounced close, not role="menu"), duplicated rather than shared because
// this trigger renders an avatar + name instead of a plain label.
.nav-account {
  position: relative;
  margin-right: 12px;
}
.nav-account-trigger {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 0 14px;
  height: 100%;
  border: 0;
  background: transparent;
  cursor: pointer;
  font-family: inherit;
  font-size: 17px;
  font-weight: 500;
  color: $ps-body-color;
  transition: color 120ms ease;

  &:hover {
    color: $ps-primary;
  }
}
.nav-account.is-open .nav-account-trigger {
  color: $ps-primary;
  font-weight: 600;
}
.nav-account-avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  background: $ps-primary-50;
  color: $ps-primary;
  font-size: $ps-text-label;
  font-weight: 700;
}
.nav-dd-chevron {
  width: 14px;
  height: 14px;
  flex: 0 0 auto;
  transition: transform 120ms ease;
}
.nav-account.is-open .nav-dd-chevron {
  transform: rotate(180deg);
}
.nav-account-panel {
  position: absolute;
  top: 100%;
  right: 0;
  z-index: 30;
  min-width: 200px;
  margin-top: 8px;
  padding: 8px;
  border-radius: $ps-radius-md;
  background: $ps-white;
  box-shadow: 0 12px 32px rgba(20, 22, 31, 0.14);

  li + li {
    margin-top: 2px;
  }

  a,
  button {
    display: block;
    width: 100%;
    padding: 10px 12px;
    border: 0;
    border-radius: $ps-radius-sm;
    background: transparent;
    font-family: inherit;
    font-size: $ps-text-small;
    font-weight: 500;
    color: $ps-body-color;
    text-align: left;
    white-space: nowrap;
    cursor: pointer;

    &:hover,
    &:focus-visible {
      background: $ps-primary-50;
      color: $ps-primary;
    }
  }
}

#navbarBasicExample {
  @include touch {
    color: $white;
    position: fixed;
    // Insets rather than 100vh: on mobile 100vh is the *large* viewport, so
    // the bottom of the panel hides behind the browser chrome.
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    z-index: 2000;
    overflow-y: auto;
    overscroll-behavior: contain;
    -webkit-overflow-scrolling: touch;
    background: $ps-primary;
    padding: 18px 20px 32px;
  }
}

// --- Mobile drawer ------------------------------------------------------
.drawer-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 14px;
}
.drawer-logo img {
  display: block;
  width: 132px;
  height: auto;
  // The wordmark is dark ink; on indigo it has to read white.
  filter: brightness(0) invert(1);
}
.drawer-close {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 50%;
  background: transparent;
  color: $white;
  cursor: pointer;

  svg {
    width: 20px;
    height: 20px;
  }
}

.drawer-cta {
  border-radius: $ps-radius-lg;
}
.drawer-cta-note {
  margin-top: 8px;
  font-size: $ps-text-label;
  text-align: center;
  color: rgba(255, 255, 255, 0.75);
}

.drawer-proof {
  display: flex;
  // Stars and sentence share a line where they fit (phones from ~414px).
  // Narrower than that the sentence drops, whole, onto its own centred line
  // under the stars instead of being squeezed into a wrapped column.
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 4px 6px;
  text-align: center;

  > span {
    white-space: nowrap;
  }
  margin: 10px 0 14px;
  padding: 8px 12px;
  border-radius: $ps-radius-pill;
  background: rgba(255, 255, 255, 0.14);
  font-size: $ps-text-label;
  color: $white;
}

.drawer-links {
  li + li {
    border-top: 1px solid rgba(255, 255, 255, 0.18);
  }

  a {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 10px 2px;
    color: $white;
  }

  // Current page reads as a filled row rather than a colour change, which
  // wouldn't show against white-on-indigo.
  a.nuxt-link-exact-active {
    .drawer-link-title {
      font-weight: 700;
    }
    .drawer-icon {
      background: $white;
      color: $ps-primary;
    }
  }
}
.drawer-icon {
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 38px;
  height: 38px;
  border-radius: $ps-radius-md;
  background: rgba(255, 255, 255, 0.14);
  color: $white;

  svg {
    width: 20px;
    height: 20px;
  }
}
.drawer-link-text {
  flex: 1 1 auto;
  min-width: 0;
}
.drawer-link-title {
  display: block;
  font-size: 17px;
  font-weight: 600;
  line-height: 1.3;
}
.drawer-link-copy {
  display: block;
  margin-top: 1px;
  font-size: $ps-text-label;
  line-height: 1.35;
  color: rgba(255, 255, 255, 0.7);
}
.drawer-chevron {
  flex: 0 0 auto;
  width: 16px;
  height: 16px;
  color: rgba(255, 255, 255, 0.55);
}

.drawer-account {
  display: flex;
  gap: 10px;
  margin-top: 16px;

  .button {
    flex: 1 1 0;
    border-color: rgba(255, 255, 255, 0.6);
    background: transparent;
    color: $white;
  }
}

.drawer-help {
  margin-top: 18px;
  padding-top: 14px;
  border-top: 1px solid rgba(255, 255, 255, 0.18);
}
.drawer-help-title {
  margin-bottom: 8px;
  font-size: $ps-text-label;
  font-weight: 600;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.6);
}
.drawer-help-row {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  padding: 9px 0;
  border: 0;
  background: transparent;
  font-size: $ps-text-small;
  color: $white;
  text-align: left;
  cursor: pointer;

  svg {
    flex: 0 0 auto;
    width: 19px;
    height: 19px;
  }
}
.drawer-help-note {
  margin-left: auto;
  font-size: $ps-text-label;
  color: rgba(255, 255, 255, 0.65);
}
</style>
